/**
 * Client HTTP de l'API SYGAP.
 *
 * Deux principes :
 *   - toute erreur remonte une `ErreurApi` typée, jamais un `Error` nu ni une
 *     promesse rejetée avec un objet quelconque (invariant « erreurs » du dépôt) ;
 *   - le **détail structuré** renvoyé par l'API est conservé. C'est lui qui porte
 *     par exemple les blocages de délivrance : l'usager doit savoir quoi
 *     régulariser, pas seulement que c'est bloqué.
 */

/** Racine de l'API. Le serveur de développement la reverse-proxie vers le port 3001. */
const RACINE_API = '/api';

/** Corps d'erreur normalisé par le filtre d'exceptions de l'API. */
export interface DetailErreurApi {
  readonly code: string;
  readonly message: string;
  readonly [cle: string]: unknown;
}

export class ErreurApi extends Error {
  readonly statut: number;
  readonly code: string;
  readonly detail: DetailErreurApi | null;

  constructor(statut: number, code: string, message: string, detail: DetailErreurApi | null) {
    super(message);
    this.name = 'ErreurApi';
    this.statut = statut;
    this.code = code;
    this.detail = detail;
  }

  /** Le jeton est absent, expiré ou falsifié : il faut se reconnecter. */
  get estNonAuthentifie(): boolean {
    return this.statut === 401;
  }

  /** Authentifié, mais le serveur refuse : rôle insuffisant ou hors périmètre. */
  get estRefuse(): boolean {
    return this.statut === 403;
  }
}

export interface OptionsAppel {
  readonly methode?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly corps?: unknown;
  /**
   * Corps envoyé **tel quel**, sans passer par JSON.
   *
   * Sert l'import CSV (US-015) : un fichier de 8 200 lignes réencodé en chaîne
   * JSON double presque de volume à l'échappement, et obligerait les directions
   * régionales à transformer leur export avant de l'envoyer. Exclusif avec
   * `corps`.
   */
  readonly corpsTexte?: { readonly contenu: string; readonly typeMime: string };
  readonly jeton?: string | null;
  readonly signal?: AbortSignal;
}

/**
 * Appelle l'API et rend le corps désérialisé.
 *
 * @throws {ErreurApi} statut hors 2xx, réponse illisible, ou réseau indisponible.
 */
export async function appelerApi<T>(chemin: string, options: OptionsAppel = {}): Promise<T> {
  const entetes: Record<string, string> = { accept: 'application/json' };
  if (options.corps !== undefined) {
    entetes['content-type'] = 'application/json';
  }
  if (options.corpsTexte !== undefined) {
    entetes['content-type'] = options.corpsTexte.typeMime;
  }
  if (options.jeton !== undefined && options.jeton !== null) {
    entetes.authorization = `Bearer ${options.jeton}`;
  }

  const corpsEnvoye =
    options.corpsTexte !== undefined
      ? options.corpsTexte.contenu
      : options.corps === undefined
        ? undefined
        : JSON.stringify(options.corps);

  let reponse: Response;
  try {
    reponse = await fetch(`${RACINE_API}${chemin}`, {
      method: options.methode ?? 'GET',
      headers: entetes,
      ...(corpsEnvoye === undefined ? {} : { body: corpsEnvoye }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (cause) {
    // Coupure réseau : fréquent sur une liaison 3G. Le dire en clair vaut mieux
    // qu'un message technique que l'agent ne peut pas exploiter.
    throw new ErreurApi(
      0,
      'SYGAP_RESEAU_INDISPONIBLE',
      'Le service est injoignable. Vérifiez votre connexion, puis réessayez.',
      cause instanceof Error ? { code: 'SYGAP_RESEAU_INDISPONIBLE', message: cause.message } : null,
    );
  }

  const texte = await reponse.text();
  const donnees: unknown = texte === '' ? null : analyserJson(texte);

  if (!reponse.ok) {
    const detail = estDetailErreur(donnees) ? donnees : null;
    throw new ErreurApi(
      reponse.status,
      detail?.code ?? `HTTP_${String(reponse.status)}`,
      // Repli : l'API n'a pas rendu de détail exploitable (panne d'un proxy,
      // erreur non normalisée). Le message doit dire quoi faire — un agent au
      // guichet ne peut rien tirer d'un « refusé » sans suite.
      detail?.message ?? messageDeRepli(reponse.status),
      detail,
    );
  }

  return donnees as T;
}

/** Fichier rendu par l'API, avec le nom qu'elle lui donne. */
export interface FichierApi {
  readonly contenu: Blob;
  readonly nomFichier: string;
}

/**
 * Télécharge un fichier de l'API.
 *
 * Une balise `<a href>` ne conviendrait pas : la route est protégée et le jeton
 * voyage dans un en-tête, que le navigateur n'ajoute pas de lui-même. Le fichier
 * est donc récupéré ici, puis remis à l'appelant qui décide quoi en faire.
 *
 * Les erreurs suivent le même chemin que les autres : l'API répond alors en
 * JSON, dont le détail structuré est conservé.
 *
 * @throws {ErreurApi} statut hors 2xx ou réseau indisponible.
 */
export async function telechargerApi(
  chemin: string,
  options: OptionsAppel = {},
): Promise<FichierApi> {
  const entetes: Record<string, string> = {};
  if (options.jeton !== undefined && options.jeton !== null) {
    entetes.authorization = `Bearer ${options.jeton}`;
  }

  let reponse: Response;
  try {
    reponse = await fetch(`${RACINE_API}${chemin}`, {
      method: options.methode ?? 'GET',
      headers: entetes,
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (cause) {
    throw new ErreurApi(
      0,
      'SYGAP_RESEAU_INDISPONIBLE',
      'Le service est injoignable. Vérifiez votre connexion, puis réessayez.',
      cause instanceof Error ? { code: 'SYGAP_RESEAU_INDISPONIBLE', message: cause.message } : null,
    );
  }

  if (!reponse.ok) {
    const donnees: unknown = analyserJson(await reponse.text());
    const detail = estDetailErreur(donnees) ? donnees : null;
    throw new ErreurApi(
      reponse.status,
      detail?.code ?? `HTTP_${String(reponse.status)}`,
      detail?.message ?? messageDeRepli(reponse.status),
      detail,
    );
  }

  return {
    contenu: await reponse.blob(),
    nomFichier: nomDepuisDisposition(reponse.headers.get('content-disposition')),
  };
}

/** Nom de fichier annoncé par l'API, ou un repli neutre si l'en-tête manque. */
function nomDepuisDisposition(disposition: string | null): string {
  const trouve = disposition?.match(/filename="([^"]+)"/);
  return trouve?.[1] ?? 'document.pdf';
}

/**
 * Message de repli lorsque l'API n'a pas rendu de détail structuré.
 *
 * Chaque cas indique une suite : se reconnecter, revenir en arrière, réessayer,
 * ou signaler. Un message qui ne dit que « refusé » laisse l'agent bloqué au
 * guichet sans savoir si la faute vient de lui ou du service.
 */
function messageDeRepli(statut: number): string {
  if (statut === 401) {
    return 'Votre session a expiré. Reconnectez-vous pour continuer.';
  }
  if (statut === 403) {
    return 'Vous n’avez pas les droits pour cette action. Vérifiez que le dossier relève bien de votre administration, ou demandez l’accès à votre responsable.';
  }
  if (statut === 404) {
    return 'Cet élément est introuvable. Il a peut-être été supprimé ou l’adresse est erronée.';
  }
  if (statut === 409) {
    return 'Cette action entre en conflit avec l’état actuel du dossier. Rechargez la page pour voir l’état à jour, puis recommencez.';
  }
  if (statut >= 500) {
    return 'Le service a rencontré une erreur. Réessayez dans un instant ; si cela persiste, signalez-le au support en précisant l’heure.';
  }
  return 'La demande n’a pas pu être traitée. Vérifiez les informations saisies, puis réessayez ; si cela persiste, signalez-le au support.';
}

function analyserJson(texte: string): unknown {
  try {
    return JSON.parse(texte) as unknown;
  } catch {
    // Une réponse non JSON sur une API JSON signale une panne d'infrastructure
    // (page d'erreur d'un proxy). On ne la relaie pas telle quelle.
    return null;
  }
}

function estDetailErreur(valeur: unknown): valeur is DetailErreurApi {
  if (typeof valeur !== 'object' || valeur === null) return false;
  const objet = valeur as Record<string, unknown>;
  return typeof objet.code === 'string' && typeof objet.message === 'string';
}
